import { useState, useRef, useEffect, useCallback } from 'react';
import './ImageUploader.css';

const ImageUploader = ({ onFilesSelected }) => {
  const [progress] = useState(0);
  const [previews, setPreviews] = useState([]);
  const fileInputRef = useRef(null);
  const dropAreaRef = useRef(null);

  const preventDefaults = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const highlight = useCallback(() => {
    dropAreaRef.current?.classList.add('highlight');
  }, []);

  const unhighlight = useCallback(() => {
    dropAreaRef.current?.classList.remove('highlight');
  }, []);

  const handleFiles = useCallback((filesToPreview) => {
    const validFiles = filesToPreview.filter(file => file.type.match('image.*'));
    const newPreviews = [];
    
    if (validFiles.length === 0) return;

    validFiles.forEach((file, index) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push({
          id: `${index}-${Date.now()}`,
          url: e.target.result,
          file
        });
        
        if (newPreviews.length === validFiles.length) {
          setPreviews(prev => [...prev, ...newPreviews]);
          // Notify parent component about all selected files
          if (onFilesSelected) {
            onFilesSelected([...previews.map(p => p.file), ...newPreviews.map(p => p.file)]);
          }
        }
      };
      reader.readAsDataURL(file);
    });
  }, [onFilesSelected, previews]);

  const removeImage = useCallback((id) => {
    setPreviews(prev => {
      const newPreviews = prev.filter(preview => preview.id !== id);
      // Notify parent about remaining files
      if (onFilesSelected) {
        onFilesSelected(newPreviews.map(p => p.file));
      }
      return newPreviews;
    });
  }, [onFilesSelected]);

  const handleDrop = useCallback((e) => {
    preventDefaults(e);
    const dt = e.dataTransfer;
    const newFiles = [...dt.files];
    handleFiles(newFiles);
  }, [handleFiles, preventDefaults]);

  const handleFileChange = useCallback((e) => {
    const newFiles = [...e.target.files];
    handleFiles(newFiles);
    // Reset input value to allow selecting same files again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [handleFiles]);

  useEffect(() => {
    const dropArea = dropAreaRef.current;
    if (!dropArea) return;

    const eventListeners = [
      { event: 'dragenter', handler: highlight },
      { event: 'dragover', handler: highlight },
      { event: 'dragleave', handler: unhighlight },
      { event: 'drop', handler: handleDrop }
    ];

    eventListeners.forEach(({ event, handler }) => {
      dropArea.addEventListener(event, handler);
    });

    return () => {
      eventListeners.forEach(({ event, handler }) => {
        dropArea.removeEventListener(event, handler);
      });
    };
  }, [highlight, unhighlight, handleDrop]);

  return (
    <div className="upload-container">      
      <div 
        className="drop-area" 
        ref={dropAreaRef}
        onDragEnter={preventDefaults}
        onDragOver={preventDefaults}
        onDragLeave={preventDefaults}
        onDrop={preventDefaults}
      >
        <p className="drop-message">Drag & drop venue images here:</p>
        <input 
          type="file" 
          id="fileElem" 
          multiple 
          accept="image/*" 
          className="file-input"
          ref={fileInputRef}
          onChange={handleFileChange}
        />
        <button 
          className="browse-btn" 
          onClick={(e) => {
            e.preventDefault();
            fileInputRef.current?.click();
          }}
        >
          Select Files
        </button>
        
        <div className="preview-container">
          {previews.map((preview) => (
            <div key={preview.id} className="preview-item">
              <img 
                src={preview.url} 
                className="preview-image" 
                alt={`Preview ${preview.id}`} 
              />
              <button 
                className="remove-btn"
                onClick={(e) => {
                  e.preventDefault();
                  removeImage(preview.id);
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </div>
      <div 
        className="progress-bar" 
        style={{ width: `${progress}%` }}
      ></div>
    </div>
  );
};

export default ImageUploader;